import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@core/services/toast.service';
import { StudentClassEnrollmentService } from '@core/services/student-class-enrollment.service';
import { take } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-edit-class-enrollment-date',
    templateUrl: './edit-class-enrollment-date.component.html',
    styleUrls: ['./edit-class-enrollment-date.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditClassEnrollmentDate implements OnInit {
  editForm: FormGroup | any;
  studentEnrollmentInfo: any;
  defaultDateError: string = '';
  agencyDateError: string = '';
  isAgencyDateError: boolean = false;
  stateId: number = 0;
  originalAgeGradeId: number = 0;
  schoolYearId: number = 0;

  constructor(
    public studentClassEnrollmentService: StudentClassEnrollmentService,
    public toastService: ToastService,
    private fb: FormBuilder,
    public dialog: MatDialogRef<EditClassEnrollmentDate>,
    @Inject(MAT_DIALOG_DATA) public editData: any,
    private translocoService: TranslocoService
  ) {}

  get classEnrollmentDate() {
    return this.editForm.get('classEnrollmentDate');
  }
  get ageGradeId() {
    return this.editForm.get('ageGradeId');
  }

  ngOnInit(): void {
    this.defaultDateError = this.translocoService.translate('user.manageEnrollment.classEnrollments.toast.dateOutsideRatingPeriod');
    this.agencyDateError = this.translocoService.translate('user.manageEnrollment.classEnrollments.toast.dateBeforeAgencyEnrollment');
    this.initForm();
  }

  initForm(): void {
    this.editForm = this.fb.group({
      classEnrollmentDate: [null, [Validators.required]],
      ageGradeId: [null],
    });

    if (this.editData) {
      this.studentEnrollmentInfo = this.editData.data;
      this.stateId = this.studentEnrollmentInfo.classEnrollmentDate.stateId;
      this.classEnrollmentDate.setValue(this.studentEnrollmentInfo.classEnrollmentDate);
      this.schoolYearId = this.studentEnrollmentInfo.schoolYearId;  

      if (this.studentEnrollmentInfo.isDualEnrollment || this.studentEnrollmentInfo.is2025Enrollment) {
        this.ageGradeId.setValue(this.studentEnrollmentInfo.ageGradeId);
        this.originalAgeGradeId = this.studentEnrollmentInfo.ageGradeId;
        this.ageGradeId.setValidators([Validators.required]);
        this.ageGradeId.updateValueAndValidity();
      }
    }
  }

  handleDate(event: any): void {
    this.classEnrollmentDate.setValue(event);
    this.isAgencyDateError = false;
    if (this.classEnrollmentDate.value && this.studentEnrollmentInfo) {
      const classEnrollmentDate = new Date(this.classEnrollmentDate.value).getTime();
      const globalRatingPeriodMinDate = new Date(this.studentEnrollmentInfo.ratingPeriodMinDate).getTime();
      const globalRatingPeriodMaxDate = new Date(this.studentEnrollmentInfo.ratingPeriodMaxDate).getTime();
      const agencyEnrollDate = new Date(this.studentEnrollmentInfo.enrollmentStartDate).getTime();
      if (classEnrollmentDate >= globalRatingPeriodMinDate &&
        classEnrollmentDate <= globalRatingPeriodMaxDate &&
        classEnrollmentDate >= agencyEnrollDate) {
        this.classEnrollmentDate.setErrors(null);
      } else {
        this.classEnrollmentDate.setErrors({invalidDates: true});
        if (classEnrollmentDate < agencyEnrollDate) this.isAgencyDateError = true;
        this.toastService.error(this.agencyDateError);
      }
    } else {
      this.toastService.error(this.defaultDateError);
    }
    this.editForm.markAsDirty();
  }

  handleAgeGrade(event: any): void {
    this.ageGradeId.setValue(event?.id);
    const { hasInputRatings, isLocked } = this.studentEnrollmentInfo;

    if ((hasInputRatings && !isLocked) || (hasInputRatings && this.originalAgeGradeId !== this.ageGradeId.value)) {
      this.toastService.error(this.translocoService.translate('user.manageEnrollment.classEnrollments.toast.ageGradeChangeWarning'));
    }
    this.editForm.markAsDirty();
  }



  onSubmit(): void {
    if (this.editForm.pristine) return;

    if (this.editForm.invalid) {
      this.toastService.error(this.translocoService.translate('user.manageEnrollment.classEnrollments.toast.formIncomplete'));
      return;
    }

    const payload = {
      id: this.studentEnrollmentInfo.classEnrollmentId,
      classEnrollmentDate: this.classEnrollmentDate.value,
      ageGradeId: this.ageGradeId.value,
    };
    this.studentClassEnrollmentService.updateClassEnrollment(payload)
      .pipe(take(1))
      .subscribe((res: boolean) => {
        this.dialog.close({ success: res});
      });
  }

}
